// BL-1061: the Inventory list's status FILTER dropdown options — the fine
// ADR-023 lot-phase vocabulary (the same the badge renders), replacing the
// retired 6-state `UNIFIED_STATUS_FILTER_OPTIONS`. Values are `LotPhaseKind`
// tokens resolved by `phasesForStatusKey` (below) server-side and offline (BL-2430).
//
// Derived from `LOT_STATUS_LEGEND` (single source of the phase labels) so the
// dropdown, the badge, and the hover legend can never disagree. `deleted` is
// excluded — soft-deleted lots/items never appear in the list — and so is every
// key in `RETIRED_STATUS_FILTER_KEYS`.

import type { LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import { LOT_STATUS_LEGEND } from './status-legend';

/**
 * BUG-1842 (Michael Lawrie, 2026-09-26): phases the Status filter never offers.
 * `awaiting-resolution` lasts the few seconds between a lot's clock reaching zero
 * and the server recording its result — a timing technicality, not a state an
 * operator filters by. The phase and its badge are untouched; only the filter
 * drops it. A stale link carrying one of these keys reads as no pick.
 */
export const RETIRED_STATUS_FILTER_KEYS: ReadonlySet<string> = new Set<LotPhaseKind>(['awaiting-resolution']);

/**
 * BUG-1774: the POST-OPEN in-flight phases the retired `live` filter key folded
 * together. The Status pill now offers each on its own; this list is what an old
 * `?status=live` link still resolves to — server-side (the filter key's phase
 * set) and client-side (the chips it reads as). BUG-1842 dropped
 * `awaiting-resolution`, which the filter no longer offers.
 */
export const LEGACY_LIVE_STATUS_PHASES = [
  'open-for-bidding',
  'last-call',
  'paused',
] as const satisfies readonly LotPhaseKind[];

/** The retired group key an old link may still carry (`?status=live`). */
export const LEGACY_LIVE_STATUS_KEY = 'live';

/** Filter-dropdown options for the inventory status filter, in lifecycle order
 *  with a leading "All Statuses". Shape matches the `Select` `options` prop. */
export const INVENTORY_STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All statuses' },
  ...LOT_STATUS_LEGEND
    .filter((e) => e.key !== 'deleted' && !RETIRED_STATUS_FILTER_KEYS.has(e.key))
    .map((e) => ({ value: e.key, label: e.label })),
];

/**
 * The status filter/count vocabulary. BL-2430 moved it here from the server-only
 * `inventory-phase-sql.ts`, so the offline Inventory query reads the same map.
 *
 * (Original notes:) Every fine phase key maps to itself and is
 * an option in the Inventory status FILTER dropdown; two coarse group keys ALSO
 * exist to fold multiple fine phases into one operator-facing COUNT chip:
 *
 * - `'live'` folds the four phases that are in-flight AFTER an auction has
 *   opened (open-for-bidding / last-call / awaiting-resolution / paused).
 *   `pre-bidding` is DELIBERATELY excluded — BL-1063 split it into its own
 *   chip to match `status-legend.ts`'s own "Before sale" grouping (BL-1061
 *   shipped it folded into `live`, contradicting its own legend).
 * - `'unsold'` is the operator-facing "Unsold" COUNT chip. It USED to fold two
 *   phases (`passed` = reserve not met, `unsold` = closed without a sale);
 *   BL-1387 PR 2 retired the `passed` phase, so there is one phase left and the
 *   fold is gone. The reserve-not-met vs no-bids DISTINCTION survives, but on
 *   `auction_lots.disposition` and in `deriveTerminalDisposition`'s `reason`,
 *   not as a phase.
 *
 * Fine key vs coarse chip are ORTHOGONAL — `last-call` / `awaiting-resolution`
 * remain standalone FILTER keys while the `live` CHIP folds them.
 *
 * The `passed` KEY IS RETAINED (BL-1387 PR 2) even though its phase is gone,
 * and this is deliberate. The dropdown still offers "Reserve not met" →
 * `?status=passed`, and BL-1061 RAC 1 exists because BL-1063 once deleted this
 * key: `phasesForStatusKey('passed')` then returned null → no WHERE clause →
 * the ENTIRE list (QA saw all 1239 items). Deleting the key now would
 * reintroduce that exact bug. It resolves to `['unsold']`.
 *
 * ⚠️ KNOWN IMPRECISION, stated rather than hidden: because the phase-level
 * distinction is retired, "Reserve not met" now returns EVERY no-sale lot,
 * including no-bids ones. Narrowing it back to genuinely reserve-not-met lots
 * means filtering on `disposition = 'passed'` (or `bids_placed_count > 0`),
 * which is a disposition-level filter this phase-keyed map cannot express —
 * a follow-up, not this card. The alternative (drop the option) removes
 * operator functionality BL-1061 deliberately restored.
 *
 * The COUNT chips read only the named bucket keys in
 * `inventory-stats-repository`, so this key adds NO chip.
 *
 * Deleted lots never appear here — soft-deleted rows get a SEPARATE count query
 * (see `deletedOnly` on `buildInventoryWhereClause`).
 */
export const INVENTORY_STATUS_FILTER_PHASES: Record<string, readonly LotPhaseKind[]> = {
  draft: ['draft'],
  ready: ['ready'],
  'pre-bidding': ['pre-bidding'],
  'open-for-bidding': ['open-for-bidding'],
  'last-call': ['last-call'],
  'awaiting-resolution': ['awaiting-resolution'],
  paused: ['paused'],
  // fine FILTER key — the "Reserve not met" dropdown option. Resolves to
  // `unsold` since BL-1387 PR 2 retired the `passed` phase. MUST stay non-empty:
  // a null/empty resolution disables the filter entirely (BL-1061 RAC 1).
  passed: ['unsold'],
  // BUG-1774: the retired "Live" group key — every POST-OPEN in-flight phase.
  // No UI offers it any more (each phase is its own option); it stays so an
  // old `?status=live` link still filters instead of returning the whole list
  // (the BL-1061 RAC 1 failure mode, same reason `passed` is kept).
  live: LEGACY_LIVE_STATUS_PHASES,
  sold: ['sold'],
  // the coarse "Unsold" count chip. Was a merge of two phases; one remains.
  unsold: ['unsold'],
  withdrawn: ['withdrawn'],
};

/**
 * Resolve a status filter key (`?status=`) to its phase set, or `null` when the
 * key is unknown / `'all'` / retired (no status filter).
 *
 * BUG-1842: a retired key (`awaiting-resolution`) keeps its map entry, because
 * the count query still buckets by it, but it no longer filters — the page reads
 * a stale link carrying it as no pick, so the list must not narrow behind a
 * chip the operator cannot see.
 */
export function phasesForStatusKey(status: string | null | undefined): readonly LotPhaseKind[] | null {
  if (!status || status === 'all' || RETIRED_STATUS_FILTER_KEYS.has(status)) return null;
  return INVENTORY_STATUS_FILTER_PHASES[status] ?? null;
}
